import { useState, useRef, useEffect, useCallback, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import type { TaskStatus } from "../../../lib/types";

/**
 * Sprint 26.0 — the task status pill. Renders one of the four task statuses
 * (inbox / queued / done / dropped) as a flat, sentence-case pill with a
 * dropdown to change it. Two weights, 0.5px borders; no runtime-status
 * semantics — this is the durable lifecycle status, not a live "active" badge.
 */

const STATUS_DOT_COLOR: Record<TaskStatus, string> = {
  inbox: "var(--c-gray-400)",
  queued: "var(--c-teal-600)",
  done: "var(--c-gray-500)",
  dropped: "var(--c-gray-400)",
};

const STATUS_LABEL: Record<TaskStatus, string> = {
  inbox: "Inbox",
  queued: "Queued",
  done: "Done",
  dropped: "Dropped",
};

const STATUS_FILL: Record<TaskStatus, { bg: string; fg: string }> = {
  inbox: { bg: "var(--color-background-secondary)", fg: "var(--color-text-secondary)" },
  queued: { bg: "var(--c-teal-50)", fg: "var(--c-teal-800)" },
  done: { bg: "var(--color-background-secondary)", fg: "var(--color-text-secondary)" },
  dropped: { bg: "var(--color-background-secondary)", fg: "var(--color-text-tertiary)" },
};

const STATUS_OPTIONS: TaskStatus[] = ["inbox", "queued", "done", "dropped"];

interface TaskStatusPillProps {
  status: TaskStatus;
  /** Omit for a read-only (display-only) pill — no dropdown. */
  onChange?: (status: TaskStatus) => void;
}

export function TaskStatusPill({ status, onChange }: TaskStatusPillProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const { bg, fg } = STATUS_FILL[status];

  const reposition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos({
      top: r.bottom + 4,
      left: Math.max(8, r.right - 180),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    reposition();
    const onClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, reposition]);

  // Read-only pill (no onChange) — a static label, no dropdown affordance. Placed
  // after all hooks so the hook order is unconditional (Rules of Hooks).
  if (!onChange) {
    return (
      <span style={{ ...pillStyle, background: bg, color: fg, cursor: "default" }}>
        {STATUS_LABEL[status]}
      </span>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        role="button"
        aria-label={`Change task status, currently ${STATUS_LABEL[status]}`}
        style={{ ...pillStyle, background: bg, color: fg }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          }
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLElement).style.filter = "brightness(0.95)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLElement).style.filter = "none";
        }}
      >
        {STATUS_LABEL[status]}
      </button>
      {open &&
        createPortal(
          <div ref={menuRef} role="listbox" aria-label="Task status options" style={{ ...popoverStyle, top: pos.top, left: pos.left }}>
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                role="option"
                aria-selected={opt === status}
                style={optionStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  if (opt !== status) onChange(opt);
                  setOpen(false);
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "var(--color-background-secondary)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = "transparent";
                }}
              >
                <span
                  style={{ ...dotStyle, background: STATUS_DOT_COLOR[opt] }}
                  aria-hidden="true"
                />
                <span style={{ flex: 1 }}>{STATUS_LABEL[opt]}</span>
                {opt === status && (
                  <i className="ti ti-check" style={{ fontSize: 14, color: "var(--color-text-tertiary)" }} aria-hidden="true" />
                )}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

const pillStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "var(--kuvio-pill-padding)",
  borderRadius: "var(--kuvio-pill-radius)",
  fontSize: 11,
  fontWeight: 500,
  fontFamily: "var(--font-sans)",
  lineHeight: 1.4,
  cursor: "pointer",
  border: "none",
  flexShrink: 0,
};

const popoverStyle: CSSProperties = {
  position: "fixed",
  zIndex: 9999,
  width: 180,
  padding: 6,
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-tertiary)",
  borderRadius: "var(--border-radius-md)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
};

const optionStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  width: "100%",
  padding: "8px 10px",
  border: "none",
  background: "transparent",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 500,
  fontFamily: "var(--font-sans)",
  color: "var(--color-text-primary)",
  borderRadius: "var(--border-radius-sm)",
  textAlign: "left",
};

const dotStyle: CSSProperties = {
  width: 7,
  height: 7,
  borderRadius: "50%",
  flexShrink: 0,
};
